/**
 * <store-locator> — postcode/suburb/state store finder with a Leaflet map.
 *
 * Store data comes from window.omfGeo.getAllStores() (assets/geolocation.js),
 * which fetches the `locations` metaobjects via the Storefront API — same
 * source the PDP postcode checker uses. Ratings come from the Cloudflare
 * Worker's Google Places proxy (data-worker-url), since that call needs a
 * server-side key and can't run client-side.
 *
 * The map is always visible on desktop (initialized with every store on
 * load, then narrowed to the active search's results); on mobile it's
 * behind the list/map tabs, revealed once results exist (host gets a
 * `has-results` class — see component-store-locator.css). By default (no
 * search yet) the list and map both show every store.
 */

if (!customElements.get('store-locator')) {
  const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
  const DAY_LABELS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  const PIN_URL = 'https://cdn.shopify.com/s/files/1/0523/6729/4661/files/omf-pin.png?v=1785399220';

  function titleCase(str) {
    if (!str) return '';
    return str.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
  }

  function storeState(store) {
    return (store.postcode || '').trim().split(' ')[0].toUpperCase();
  }

  function mapsSearchLink(store) {
    const query = `${store.name} ${store.address || ''}`;
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
  }

  function tradingHoursOptions(store) {
    const todayJs = new Date().getDay();
    const todayIndex = todayJs === 0 ? 6 : todayJs - 1;

    return DAYS.map((day, index) => {
      const hours = store.hours && store.hours[day] ? store.hours[day] : 'Closed';
      const selected = index === todayIndex ? ' selected' : '';
      return `<option value="${index}"${selected}>${DAY_LABELS[index]}: ${hours}</option>`;
    }).join('');
  }

  function starRating(rating, reviewCount) {
    if (!rating) {
      return '<span class="store-locator__card-rating-empty">No reviews yet</span>';
    }
    const full = Math.floor(rating);
    const half = rating % 1 >= 0.5;
    const empty = 5 - full - (half ? 1 : 0);
    const stars = '★'.repeat(full) + (half ? '★' : '') + '☆'.repeat(empty);
    return `<span class="store-locator__card-stars">${stars}</span><span class="store-locator__card-rating-count">${rating.toFixed(1)} from ${reviewCount || 0} reviews</span>`;
  }

  class StoreLocator extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('[data-form]');
      this.input = this.querySelector('[data-input]');
      this.suggestionsEl = this.querySelector('[data-suggestions]');
      this.locationBtn = this.querySelector('[data-use-location]');
      this.stateButtons = this.querySelectorAll('[data-state]');
      this.statusEl = this.querySelector('[data-status]');
      this.tabsEl = this.querySelector('[data-tabs]');
      this.tabButtons = this.querySelectorAll('[data-tab]');
      this.listEl = this.querySelector('[data-list]');
      this.mapEl = this.querySelector('[data-map]');

      this.workerUrl = this.dataset.workerUrl || '';
      this.allStores = null;
      this.map = null;
      this.markers = [];
      this.suggestions = [];
      this.highlightedIndex = -1;
      this.debounceTimer = null;

      if (!this.form || !this.input || !this.listEl || !this.mapEl) return;

      this.form.addEventListener('submit', (e) => {
        e.preventDefault();
        this.hideSuggestions();
        this.handleSearchSubmit(this.input.value);
      });

      this.input.addEventListener('input', (e) => this.debounceSuggestions(e.target.value));
      this.input.addEventListener('keydown', (e) => this.handleInputKeydown(e));
      this.input.addEventListener('focus', () => {
        if (this.input.value.trim().length >= 2) this.fetchSuggestions(this.input.value.trim());
      });

      this.outsideClickHandler = (e) => {
        if (!this.contains(e.target)) this.hideSuggestions();
      };
      document.addEventListener('click', this.outsideClickHandler);

      this.locationBtn?.addEventListener('click', () => this.searchByLocation());

      this.stateButtons.forEach((btn) => {
        btn.addEventListener('click', () => this.searchByState(btn.dataset.state, btn));
      });

      this.tabButtons.forEach((btn) => {
        btn.addEventListener('click', () => this.setMobileTab(btn.dataset.tab));
      });

      const params = new URLSearchParams(window.location.search);
      const state = params.get('state');
      const query = params.get('q');

      if (state) {
        const btn = Array.from(this.stateButtons).find((b) => b.dataset.state === state);
        if (btn) this.searchByState(state, btn);
      } else if (query) {
        this.input.value = query;
        this.handleSearchSubmit(query);
      } else {
        this.showDefaultView();
      }
    }

    disconnectedCallback() {
      if (this.outsideClickHandler) document.removeEventListener('click', this.outsideClickHandler);
    }

    // ── Data ─────────────────────────────────────────────────────────────

    async getStores() {
      if (this.allStores) return this.allStores;
      if (!window.omfGeo) {
        this.allStores = [];
        return this.allStores;
      }
      this.allStores = await window.omfGeo.getAllStores();
      return this.allStores;
    }

    async showDefaultView() {
      const stores = await this.getStores();
      if (!stores.length) return;

      this.renderResults(stores, {
        heading: `${stores.length} Stores`,
        showDistance: false,
      });
    }

    async nearestStores(lat, lng, limit = 3) {
      const stores = await this.getStores();
      return stores
        .filter((store) => store.lat !== null && store.lng !== null)
        .map((store) => ({ ...store, distance: window.omfGeo.calculateDistance(lat, lng, store.lat, store.lng) }))
        .sort((a, b) => a.distance - b.distance)
        .slice(0, limit);
    }

    // ── URL state ────────────────────────────────────────────────────────

    updateUrl(params) {
      const url = new URL(window.location);
      url.search = '';
      Object.keys(params).forEach((key) => {
        if (params[key]) url.searchParams.set(key, params[key]);
      });
      window.history.replaceState({}, '', url);
    }

    // ── Live suggestions ─────────────────────────────────────────────────

    debounceSuggestions(query) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        if (query.trim().length >= 2) {
          this.fetchSuggestions(query.trim());
        } else {
          this.hideSuggestions();
        }
      }, 150);
    }

    async fetchSuggestions(query) {
      if (!window.omfGeo) return;
      const data = await window.omfGeo.getPostcodeData();
      const q = query.toUpperCase();
      const isNumeric = /^\d+$/.test(q);
      const results = [];

      for (let i = 0; i < data.length && results.length < 8; i++) {
        const row = data[i];
        const suburb = (row.Suburb || '').toUpperCase();
        const postcode = (row.Postocode || '').toUpperCase();
        const match = isNumeric ? postcode.startsWith(q) : suburb.startsWith(q) || suburb.includes(` ${q}`);
        if (match && parseFloat(row.Longitude) > 0) results.push(row);
      }

      this.renderSuggestions(results);
    }

    renderSuggestions(matches) {
      this.suggestions = matches;
      this.highlightedIndex = -1;

      if (!matches.length) {
        this.hideSuggestions();
        return;
      }

      this.suggestionsEl.innerHTML = '';

      matches.forEach((match) => {
        const item = document.createElement('button');
        item.type = 'button';
        item.className = 'store-locator__suggestion-item';
        item.textContent = `${titleCase(match.Suburb)}, ${match.State} ${match.Postocode}`;
        item.addEventListener('click', () => this.selectSuggestion(match));
        this.suggestionsEl.appendChild(item);
      });

      this.suggestionsEl.hidden = false;
    }

    hideSuggestions() {
      this.suggestionsEl.hidden = true;
      this.suggestionsEl.innerHTML = '';
      this.suggestions = [];
      this.highlightedIndex = -1;
    }

    selectSuggestion(match) {
      this.input.value = `${titleCase(match.Suburb)}, ${match.State} ${match.Postocode}`;
      this.hideSuggestions();
      this.clearActiveState();
      this.updateUrl({ q: match.Postocode });
      this.searchNear(match);
    }

    handleInputKeydown(e) {
      if (this.suggestionsEl.hidden || !this.suggestions.length) return;
      const items = Array.from(this.suggestionsEl.querySelectorAll('.store-locator__suggestion-item'));

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        this.highlightedIndex = Math.min(this.highlightedIndex + 1, items.length - 1);
        this.updateHighlight(items);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        this.highlightedIndex = Math.max(this.highlightedIndex - 1, -1);
        this.updateHighlight(items);
      } else if (e.key === 'Enter') {
        if (this.highlightedIndex >= 0 && this.suggestions[this.highlightedIndex]) {
          e.preventDefault();
          this.selectSuggestion(this.suggestions[this.highlightedIndex]);
        }
      } else if (e.key === 'Escape') {
        this.hideSuggestions();
      }
    }

    updateHighlight(items) {
      items.forEach((item, index) => item.classList.toggle('is-highlighted', index === this.highlightedIndex));
    }

    // ── Searches ─────────────────────────────────────────────────────────

    async handleSearchSubmit(rawQuery) {
      const query = (rawQuery || '').trim();
      if (!query) {
        this.showStatus('Please enter a postcode or suburb.', true);
        return;
      }

      this.clearActiveState();

      if (!window.omfGeo) {
        this.showStatus('Store search is unavailable right now. Please try again shortly.', true);
        return;
      }

      const isPostcode = /^\d+$/.test(query);

      if (isPostcode) {
        if (query.length !== 4) {
          this.showStatus('Please enter a valid 4-digit postcode.', true);
          return;
        }

        await this.handlePostcodeQuery(query);
        return;
      }

      // Selecting a suggestion writes the fully-formatted "Suburb, STATE 1234" string into the
      // input (see selectSuggestion). If the user then hits "Find Stores" without editing that
      // text, it isn't a bare postcode, but it still contains one — search by that first, since
      // it's the more precise match, before falling back to suburb-name matching.
      const embeddedPostcode = query.match(/\b\d{4}\b/);
      if (embeddedPostcode) {
        await this.handlePostcodeQuery(embeddedPostcode[0]);
        return;
      }

      // Suburb-name search. Strip a trailing ", STATE" (or similar) suffix so a previously
      // selected suggestion's display text still matches by suburb name alone.
      const suburbQuery = query.split(',')[0].trim();

      this.updateUrl({ q: suburbQuery });
      this.showLoading();

      const data = await window.omfGeo.getPostcodeData();
      const q = suburbQuery.toUpperCase();
      const matches = data
        .filter((entry) => {
          const suburb = (entry.Suburb || '').toUpperCase();
          return (suburb.startsWith(q) || suburb.includes(` ${q}`)) && parseFloat(entry.Longitude) > 0;
        })
        .slice(0, 8);

      if (!matches.length) {
        this.showStatus('No matching suburb found. Please check and try again.', true);
        return;
      }

      if (matches.length === 1) {
        await this.searchNear(matches[0]);
      } else {
        this.statusEl.hidden = true;
        this.renderSuggestions(matches);
      }
    }

    async handlePostcodeQuery(postcode) {
      this.updateUrl({ q: postcode });
      this.showLoading();

      const matches = await window.omfGeo.getPostcodeMatches(postcode);
      const valid = matches.filter((m) => parseFloat(m.Longitude) > 0);

      if (!valid.length) {
        this.showStatus('Postcode not found. Please check and try again.', true);
        return;
      }

      if (valid.length === 1) {
        await this.searchNear(valid[0]);
      } else {
        this.statusEl.hidden = true;
        this.renderSuggestions(valid);
      }
    }

    async searchNear(place) {
      this.showLoading();

      const lat = parseFloat(place.Latitude);
      const lng = parseFloat(place.Longitude);
      const nearest = await this.nearestStores(lat, lng);

      if (!nearest.length) {
        this.showStatus('No stores found.', true);
        return;
      }

      this.renderResults(nearest, {
        heading: `${nearest.length} Nearest Store${nearest.length > 1 ? 's' : ''}`,
        subheading: `Near ${titleCase(place.Suburb)}, ${place.State} ${place.Postocode}`,
        showDistance: true,
      });
    }

    async searchByLocation() {
      if (!navigator.geolocation) {
        this.showStatus('Geolocation is not supported by your browser.', true);
        return;
      }

      this.clearActiveState();
      this.showLoading();

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude: lat, longitude: lng } = position.coords;
          const nearest = await this.nearestStores(lat, lng);

          if (!nearest.length) {
            this.showStatus('No stores found.', true);
            return;
          }

          this.updateUrl({});
          this.renderResults(nearest, {
            heading: `${nearest.length} Nearest Store${nearest.length > 1 ? 's' : ''}`,
            subheading: 'Using your current location',
            showDistance: true,
          });
        },
        () => {
          this.showStatus('Could not detect your location. Please enter a postcode or suburb instead.', true);
        },
        { enableHighAccuracy: false, timeout: 10000, maximumAge: 0 }
      );
    }

    async searchByState(state, btn) {
      this.clearActiveState();
      btn?.classList.add('is-active');
      this.input.value = '';
      this.updateUrl({ state });
      this.showLoading();

      const stores = await this.getStores();
      const matches = stores.filter((store) => storeState(store) === state);

      if (!matches.length) {
        this.showStatus(`No stores found in ${state}.`, true);
        return;
      }

      this.renderResults(matches, {
        heading: `${matches.length} Store${matches.length > 1 ? 's' : ''} in ${state}`,
        showDistance: false,
      });
    }

    clearActiveState() {
      this.stateButtons.forEach((b) => b.classList.remove('is-active'));
      this.hideSuggestions();
    }

    // ── Rendering ────────────────────────────────────────────────────────

    showLoading() {
      this.statusEl.hidden = false;
      this.statusEl.classList.remove('is-error');
      this.statusEl.innerHTML = '<span class="store-locator__spinner"></span> Finding stores…';
      this.listEl.innerHTML = '';
    }

    showStatus(message, isError) {
      this.statusEl.hidden = false;
      this.statusEl.classList.toggle('is-error', !!isError);
      this.statusEl.textContent = message;
      this.listEl.innerHTML = '';
    }

    renderResults(stores, { heading, subheading, showDistance }) {
      this.statusEl.hidden = false;
      this.statusEl.classList.remove('is-error');
      this.statusEl.innerHTML = `<strong>${heading}</strong>${subheading ? ` — ${subheading}` : ''}`;

      this.classList.add('has-results');
      this.tabsEl.hidden = false;

      this.listEl.innerHTML = '';

      stores.forEach((store, index) => {
        const card = document.createElement('div');
        card.className = 'store-locator__card';
        card.id = `StoreLocatorCard-${index}`;
        card.dataset.index = index;

        const mapsLink = store.mapsLink || mapsSearchLink(store);

        card.innerHTML = `
          <div class="store-locator__card-header">
            <h3 class="store-locator__card-name">${store.name}</h3>
          </div>
          <div class="store-locator__card-rating" data-rating>Loading rating…</div>
          <div class="store-locator__card-info">
            ${store.address ? `<p><strong>Address:</strong> ${store.address}, ${store.city || ''} ${store.postcode || ''}</p>` : ''}
            ${store.phone ? `<p><strong>Phone:</strong> ${store.phone}</p>` : ''}
            ${store.email ? `<p><strong>Email:</strong> ${store.email}</p>` : ''}
            ${showDistance && store.distance !== undefined ? `<p><strong>Distance:</strong> ${store.distance.toFixed(1)} km</p>` : ''}
          </div>
          <div class="store-locator__card-hours">
            <span class="store-locator__card-hours-label">Trading hours</span>
            <select class="store-locator__card-hours-select">${tradingHoursOptions(store)}</select>
          </div>
          <div class="store-locator__card-actions">
            <a href="${store.pageUrl || '#'}" class="store-locator__card-btn store-locator__card-btn--primary">More Info</a>
            <a href="${mapsLink}" target="_blank" rel="noopener" class="store-locator__card-btn store-locator__card-btn--secondary">Open Maps</a>
          </div>
        `;

        card.addEventListener('click', (e) => {
          if (e.target.closest('a, select')) return;
          this.highlightMarker(index);
        });

        this.listEl.appendChild(card);

        if (store.placeId) {
          this.fetchRating(store.placeId, card);
        } else {
          card.querySelector('[data-rating]').innerHTML = '<span class="store-locator__card-rating-empty">No reviews yet</span>';
        }
      });

      this.initMap(stores);
    }

    async fetchRating(placeId, card) {
      if (!this.workerUrl) return;
      try {
        const res = await fetch(`${this.workerUrl}/places/${encodeURIComponent(placeId)}`);
        const place = await res.json();
        const ratingEl = card.querySelector('[data-rating]');
        if (ratingEl) ratingEl.innerHTML = starRating(place.rating, place.userRatingCount);

        if (place.googleMapsURI) {
          const mapsBtn = card.querySelector('.store-locator__card-btn--secondary');
          if (mapsBtn) mapsBtn.href = place.googleMapsURI;
        }
      } catch (e) {
        console.error('Store locator: failed to fetch rating', e);
      }
    }

    // ── Mobile list/map tabs ────────────────────────────────────────────

    setMobileTab(tab) {
      this.tabButtons.forEach((btn) => btn.classList.toggle('is-active', btn.dataset.tab === tab));
      this.classList.toggle('is-showing-map', tab === 'map');

      if (tab === 'map' && this.map) {
        setTimeout(() => this.map.invalidateSize(), 100);
      }
    }

    // ── Leaflet map ──────────────────────────────────────────────────────

    initMap(stores) {
      if (typeof L === 'undefined') return;

      this.markers.forEach((marker) => this.map?.removeLayer(marker));
      this.markers = [];

      const valid = stores.filter((store) => store.lat !== null && store.lng !== null);
      if (!valid.length) return;

      if (!this.map) {
        this.map = L.map(this.mapEl).setView([valid[0].lat, valid[0].lng], 10);
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
          maxZoom: 19,
        }).addTo(this.map);
      }

      const bounds = L.latLngBounds();
      const icon = L.icon({
        iconUrl: PIN_URL,
        iconSize: [36, 36],
        iconAnchor: [18, 18],
        popupAnchor: [0, -18],
      });

      valid.forEach((store, index) => {
        const position = [store.lat, store.lng];
        const marker = L.marker(position, { icon }).addTo(this.map);
        marker.bindPopup(
          `<strong>${store.name}</strong>${store.address ? `<br>${store.address}, ${store.city || ''} ${store.postcode || ''}` : ''}${store.phone ? `<br>${store.phone}` : ''}`
        );
        marker.on('click', () => this.highlightMarker(index, false));

        this.markers.push(marker);
        bounds.extend(position);
      });

      if (valid.length > 1) {
        this.map.fitBounds(bounds, { padding: [50, 50], maxZoom: 15 });
      } else {
        this.map.setView([valid[0].lat, valid[0].lng], 13);
      }

      setTimeout(() => this.map.invalidateSize(), 200);
    }

    highlightMarker(index, panMap = true) {
      this.listEl.querySelectorAll('.store-locator__card').forEach((card) => card.classList.remove('is-selected'));
      const card = document.getElementById(`StoreLocatorCard-${index}`);
      if (card) {
        card.classList.add('is-selected');
        card.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }

      const marker = this.markers[index];
      if (marker && this.map) {
        marker.openPopup();
        if (panMap) this.map.setView(marker.getLatLng(), Math.max(this.map.getZoom(), 13), { animate: true });
      }
    }
  }

  customElements.define('store-locator', StoreLocator);
}
